import { useId, useState } from "react";
import { useAction } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { requiresProjectAssignment } from "@constructionautomation/backend/convex/lib/orgRoles";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InviteProjectPicker } from "@/features/onboarding/invite-project-picker";
import { handleOrganizationAccessError } from "@/lib/organization-access-errors";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { cn } from "@/lib/utils";
import { ROLE_DESCRIPTIONS, ROLE_LABELS, getMemberChangeErrorMessage } from "./member-change-copy";
import { SeatBillingNotice } from "./seat-billing-notice";
import { useInviteBillingImpact, type InviteRole } from "./use-seat-billing-impact";

const INVITE_ROLES: InviteRole[] = ["member", "admin", "reviewer"];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Invites one person by email, with their role and, for members and reviewers, initial projects.
 * Before sending it shows what accepting means for billing; paid roles can't be invited while a
 * cancellation is scheduled.
 */
export function InviteMemberDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: () => void;
}) {
  const inviteMember = useAction(api.authActions.inviteOrganizationMember);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<InviteRole>("member");
  const [projectIds, setProjectIds] = useState<Id<"projects">[]>([]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailId = useId();
  const emailErrorId = useId();
  const billing = useInviteBillingImpact(role, { enabled: props.open });

  const reset = () => {
    setEmail("");
    setRole("member");
    setProjectIds([]);
    setEmailError(null);
    setSubmitError(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (isSubmitting) return;
    if (!open) reset();
    props.onOpenChange(open);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError(trimmed ? "Enter a valid email address, like name@company.com." : "Enter an email address.");
      return;
    }
    setEmailError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const result = await inviteMember({
        email: trimmed,
        role,
        projectIds: requiresProjectAssignment(role) && projectIds.length > 0 ? projectIds : undefined,
      });
      toast.success(`Invitation sent to ${result.invitation.email}`);
      props.onInvited();
      reset();
      props.onOpenChange(false);
    } catch (error) {
      console.error("Invite error:", error);
      if (!handleOrganizationAccessError(error)) {
        setSubmitError(
          getMemberChangeErrorMessage(error, getUserFacingErrorMessage(error, "The invitation couldn't be sent. Try again."))
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={(event) => void handleSubmit(event)} noValidate className="space-y-5">
          <DialogHeader>
            <DialogTitle>Add member</DialogTitle>
            <DialogDescription>They'll get an email invitation. Pending invitations never use a paid seat.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor={emailId}>Email</Label>
            <Input
              id={emailId}
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) setEmailError(null);
              }}
              disabled={isSubmitting}
              autoComplete="off"
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? emailErrorId : undefined}
              autoFocus
            />
            {emailError ? (
              <p id={emailErrorId} className="text-sm font-medium text-destructive">{emailError}</p>
            ) : null}
          </div>

          <fieldset className="space-y-2" disabled={isSubmitting}>
            <legend className="mb-2 text-sm font-medium">Role</legend>
            {INVITE_ROLES.map((option) => (
              <label
                key={option}
                className={cn(
                  "flex cursor-pointer items-start gap-3 rounded-md border-2 px-3 py-2.5 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
                  role === option ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                )}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={option}
                  checked={role === option}
                  onChange={() => setRole(option)}
                  className="mt-1 size-4 accent-primary"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{ROLE_LABELS[option]}</span>
                  <span className="block text-sm text-muted-foreground">{ROLE_DESCRIPTIONS[option]}</span>
                </span>
              </label>
            ))}
          </fieldset>

          {billing.message ? <SeatBillingNotice message={billing.message} blocked={billing.blocked} /> : null}

          <InviteProjectPicker role={role} selected={projectIds} onChange={setProjectIds} disabled={isSubmitting} />

          {submitError ? (
            <p className="text-sm font-medium text-destructive" role="alert">{submitError}</p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || billing.blocked}>
              {isSubmitting && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Send invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
